/**
 * A recurring export, from the form to `ReportScheduleCreateRequest`.
 *
 * ── Why the rules are here and not learned from a `400` ─────────────────────
 *
 * `wp_report_schedules` carries four CHECK constraints that an operator would
 * otherwise meet as a refusal naming a column they never typed. Each is stated on the
 * form and enforced here, and none of this is a second authorisation path: the server
 * refuses regardless, and this exists so the person learns the rule from the field
 * rather than from a rejection. The same argument `access/reason.ts` makes about
 * `MIN_ACCESS_REASON`, and Task 45's about the revoke reason.
 *
 * ── The four, and what each is actually protecting ──────────────────────────
 *
 *  1. **`day_of_week` belongs to `weekly` and `day_of_month` to `monthly`, and each is
 *     *refused* on the other two frequencies** (`wp_sched_weekly_ck`,
 *     `wp_sched_monthly_ck`). So the field is **absent** for a frequency it does not
 *     belong to, not disabled: for a daily schedule it is not a field the person is
 *     unqualified to fill in, it is a field that does not exist.
 *  2. **`day_of_month` stops at 28. 29 to 31 are refused, not clamped.** Clamping is
 *     the bug the constraint exists to prevent: "monthly on the 31st" would run on the
 *     31st, 30th, 29th or 28th depending on the month, with the row still saying 31,
 *     and a payroll report would move by up to three days four times a year with
 *     nothing on screen admitting it. "The last day of the month" is a different
 *     frequency and is not in this build.
 *  3. **`name` may not contain a line break.** It leads the notification's subject
 *     line, and a mail header ends at a CRLF — a name carrying one would become extra
 *     headers. This is a header-injection refusal wearing a length limit's clothes,
 *     which is why it is checked rather than trimmed.
 *  4. **`formats` may not repeat.** One `wp_reports` row and one object key per
 *     format per run; two rows for one key would overwrite each other in silence.
 *
 * And the fifth rule, which is not a CHECK but is the one an operator is most likely
 * to be surprised by: **`recipients` are `wp_employees.id` of this company, never
 * email addresses.** The only path from an id to a mailbox is a statement carrying
 * `company_id`, so "outside this company" is a query that returns no rows rather than
 * a rule somebody remembered to write. A schedule that accepted addresses would be a
 * standing, addressed data-export channel to any mailbox on the internet.
 */

import type {
  ReportFormatName,
  ReportScheduleCreateRequest,
  ReportTypeName,
  SchedulePeriod,
  ScheduleFrequency,
} from '../api/types';

export const SCHEDULE_NAME_MAX = 120;
export const SCHEDULE_RECIPIENTS_MAX = 50;
/** `wp_sched_monthly_ck`. Twenty-eight, and 29 to 31 are refused rather than moved. */
export const SCHEDULE_DAY_OF_MONTH_MAX = 28;

export const SCHEDULE_FREQUENCIES: readonly ScheduleFrequency[] = ['daily', 'weekly', 'monthly'];

/** Every one of these windows ends **yesterday**: a run that included today would
 *  present a partial day as a whole one. */
export const SCHEDULE_PERIODS: readonly SchedulePeriod[] = [
  'previous_day',
  'previous_week',
  'previous_month',
  'rolling_7d',
  'rolling_30d',
];

/** What the operator has typed. Strings, because that is what an input holds. */
export interface ScheduleDraft {
  readonly name: string;
  readonly reportType: ReportTypeName;
  readonly formats: readonly ReportFormatName[];
  readonly frequency: ScheduleFrequency;
  readonly dayOfWeek: string;
  readonly dayOfMonth: string;
  readonly hourOfDay: string;
  readonly minuteOfHour: string;
  readonly timezone: string;
  readonly period: SchedulePeriod;
  /** Comma or whitespace separated employee ids, exactly as typed. */
  readonly recipients: string;
  readonly enabled: boolean;
}

/** One reason this draft would be refused, named by the field it is about. */
export interface ScheduleProblem {
  readonly field:
    | 'name'
    | 'formats'
    | 'day_of_week'
    | 'day_of_month'
    | 'hour_of_day'
    | 'minute_of_hour'
    | 'recipients';
  readonly code: string;
}

/**
 * The ids in the recipients field, split and de-duplicated, in the order typed.
 *
 * Split on commas **and** whitespace, because somebody pasting a column out of a
 * spreadsheet gets newlines and somebody typing gets commas, and a form that accepted
 * only one of those would refuse a perfectly good list for a reason the person could
 * not see. Empty fragments are dropped rather than sent: a trailing comma is a typing
 * artefact and not a request to mail nobody.
 */
export function parseRecipients(raw: string): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\s,]+/)) {
    const id = part.trim();
    if (id === '' || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Does this frequency have a day field at all? Drives absence, not disabling. */
export function dayFieldFor(frequency: ScheduleFrequency): 'day_of_week' | 'day_of_month' | null {
  if (frequency === 'weekly') return 'day_of_week';
  if (frequency === 'monthly') return 'day_of_month';
  return null;
}

function wholeNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * Everything the server would refuse, before it is sent.
 *
 * Returned as a list rather than a first-failure, because a form that reveals one
 * problem per submit is a form somebody presses five times. Each entry names the
 * field, so the message lands beside the input it is about.
 */
export function scheduleProblems(draft: ScheduleDraft): readonly ScheduleProblem[] {
  const problems: ScheduleProblem[] = [];
  const name = draft.name.trim();
  if (name.length === 0 || name.length > SCHEDULE_NAME_MAX) {
    problems.push({ field: 'name', code: 'length' });
  } else if (/[\r\n]/.test(draft.name)) {
    // Checked on the raw value, not the trimmed one: `trim` removes a trailing CRLF
    // and would hide the very thing this rule exists to catch.
    problems.push({ field: 'name', code: 'line_break' });
  }

  if (draft.formats.length === 0 || draft.formats.length > 3) {
    problems.push({ field: 'formats', code: 'count' });
  } else if (new Set(draft.formats).size !== draft.formats.length) {
    problems.push({ field: 'formats', code: 'duplicate' });
  }

  const day = dayFieldFor(draft.frequency);
  if (day === 'day_of_week') {
    const value = wholeNumber(draft.dayOfWeek);
    if (value === null || value < 1 || value > 7) {
      problems.push({ field: 'day_of_week', code: 'range' });
    }
  }
  if (day === 'day_of_month') {
    const value = wholeNumber(draft.dayOfMonth);
    if (value === null || value < 1 || value > SCHEDULE_DAY_OF_MONTH_MAX) {
      // 29, 30 and 31 land here and that is the point: refused, never moved to 28.
      problems.push({ field: 'day_of_month', code: 'range' });
    }
  }

  const hour = wholeNumber(draft.hourOfDay);
  if (hour === null || hour > 23) problems.push({ field: 'hour_of_day', code: 'range' });
  const minute = wholeNumber(draft.minuteOfHour);
  if (minute === null || minute > 59) problems.push({ field: 'minute_of_hour', code: 'range' });

  const recipients = parseRecipients(draft.recipients);
  if (recipients.length === 0 || recipients.length > SCHEDULE_RECIPIENTS_MAX) {
    problems.push({ field: 'recipients', code: 'count' });
  }

  return problems;
}

/**
 * `ReportScheduleCreateRequest`, built from a draft that has no problems.
 *
 * **An absent field is an omitted member, never `null` and never `''`.** The schema
 * is `additionalProperties: false`, and `day_of_week` on a daily schedule is not
 * "unset" — it is refused by `wp_sched_weekly_ck`. Sending `null` for it would be
 * this client asserting a value the constraint has an opinion about; omitting it is
 * the request the constraint was written for. The same reasoning `ExportSlot`'s
 * `exportRequestBody` records for `employee_ids`.
 *
 * `timezone` is the one place `null` is *meaningful* and is therefore sent: the
 * schema says `null` inherits the company's own zone, which is a different request
 * from naming a zone. An empty field means "inherit", and that is `null`, not an
 * omission — though the two behave the same today, the schema documents only one of
 * them, and a client should send the one that is documented.
 *
 * Exported so a test can compare its **keys** against the schema in `openapi.yaml`
 * rather than against a rendered string. `ExportSlot` shipped a body wrong in five
 * places precisely because its only reader was a `<code>` block, and a rendered
 * string is not a check.
 */
export function scheduleRequestBody(draft: ScheduleDraft): ReportScheduleCreateRequest {
  const day = dayFieldFor(draft.frequency);
  return {
    name: draft.name.trim(),
    report_type: draft.reportType,
    formats: draft.formats,
    frequency: draft.frequency,
    ...(day === 'day_of_week' ? { day_of_week: Number(draft.dayOfWeek.trim()) } : {}),
    ...(day === 'day_of_month' ? { day_of_month: Number(draft.dayOfMonth.trim()) } : {}),
    hour_of_day: Number(draft.hourOfDay.trim()),
    minute_of_hour: Number(draft.minuteOfHour.trim()),
    timezone: draft.timezone.trim() === '' ? null : draft.timezone.trim(),
    period: draft.period,
    recipients: parseRecipients(draft.recipients),
    enabled: draft.enabled,
  };
}
